import {
    EnvironmentProviders,
    inject,
    PLATFORM_ID,
    provideAppInitializer,
    REQUEST,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { InterpolatableTranslationObject, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { readStoredLanguage } from '@core/i18n/language-storage';

/** Languages the application ships translations for. */
export const SUPPORTED_LANGUAGES: ReadonlyArray<string> = ['en', 'de'];

/** Used when the requested browser language is not supported. */
export const DEFAULT_LANGUAGE: string = 'de';

/** Whether the given code is one of the {@link SUPPORTED_LANGUAGES}. */
export function isSupportedLanguage(code: string | null | undefined): code is string {
    return !!code && SUPPORTED_LANGUAGES.includes(code);
}

/**
 * Picks the first supported language from an ordered list of candidates (most preferred first),
 * falling back to {@link DEFAULT_LANGUAGE} when none of them are supported.
 */
export function resolveLanguage(candidates: ReadonlyArray<string>): string {
    for (const candidate of candidates) {
        const code: string = candidate.split('-')[0].toLowerCase();

        if (isSupportedLanguage(code)) {
            return code;
        }
    }

    return DEFAULT_LANGUAGE;
}

/** A parsed `Accept-Language` entry: a language code with its `q` quality weight. */
interface AcceptLanguageEntry {
    readonly code: string;
    readonly quality: number;
}

/**
 * Parses an HTTP `Accept-Language` header into an ordered list of language codes, most preferred
 * first (respecting the optional `q` quality weights).
 */
export function parseAcceptLanguage(header: string | null | undefined): ReadonlyArray<string> {
    if (!header) {
        return [];
    }

    return header
        .split(',')
        .map((part: string): AcceptLanguageEntry => {
            const [code, quality] = part.trim().split(';q=');

            return { code: code.trim(), quality: quality ? Number(quality) : 1 };
        })
        .filter((entry: AcceptLanguageEntry): boolean => entry.code.length > 0)
        .sort((a: AcceptLanguageEntry, b: AcceptLanguageEntry): number => b.quality - a.quality)
        .map((entry: AcceptLanguageEntry): string => entry.code);
}

/**
 * Resolves the preferred language with the precedence: previously stored choice (`localStorage`,
 * client only) → environment (`navigator` on the client, `Accept-Language` header during SSR) →
 * `de` fallback. Must be called within an injection context (e.g. a route guard, redirect function
 * or app initializer).
 */
export function detectPreferredLanguage(): string {
    const stored: string | null = readStoredLanguage();

    if (isSupportedLanguage(stored)) {
        return stored;
    }

    const platformId: object = inject(PLATFORM_ID);
    const request: Request | null = inject(REQUEST, { optional: true });

    const candidates: ReadonlyArray<string> = isPlatformBrowser(platformId)
        ? navigatorLanguages()
        : parseAcceptLanguage(request?.headers.get('accept-language'));

    return resolveLanguage(candidates);
}

/**
 * Resolves the active language the same way {@link provideAppLanguage} applies it: saved preference
 * (`localStorage`/cookie) → URL prefix → browser/`Accept-Language` → `de`. Used both to pre-load the
 * language and to seed {@link LanguageService}'s signal, so the two never disagree. Must be called
 * within an injection context.
 */
export function resolveActiveLanguage(): string {
    const stored: string | null = readStoredLanguage();

    return isSupportedLanguage(stored)
        ? stored
        : (currentUrlLanguage() ?? detectPreferredLanguage());
}

/**
 * Pre-loads the active language before the app shell renders so the header/footer have their
 * translations on first paint. Precedence matches the rest of the app: saved preference → URL
 * prefix → browser/`Accept-Language` → `de`. The route guard keeps it in sync afterwards.
 */
export function provideAppLanguage(): EnvironmentProviders {
    return provideAppInitializer((): Promise<InterpolatableTranslationObject> => {
        const translate: TranslateService = inject(TranslateService);
        const document: Document = inject(DOCUMENT);

        const language: string = resolveActiveLanguage();

        document.documentElement.lang = language;

        return firstValueFrom(translate.use(language));
    });
}

function navigatorLanguages(): ReadonlyArray<string> {
    if (typeof navigator === 'undefined') {
        return [];
    }

    return navigator.languages?.length ? Array.from(navigator.languages) : [navigator.language];
}

function currentUrlLanguage(): string | null {
    const platformId: object = inject(PLATFORM_ID);

    let pathname: string;

    if (isPlatformBrowser(platformId)) {
        pathname = inject(DOCUMENT).location.pathname;
    } else {
        const request: Request | null = inject(REQUEST, { optional: true });
        pathname = request ? new URL(request.url).pathname : '';
    }

    const segment: string | undefined = pathname.split('/').filter(Boolean)[0];

    return isSupportedLanguage(segment) ? segment : null;
}
